import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { HttpStatusCode } from 'axios';
import {
  MaintenanceRecordWithDisplayName,
  MaintenanceReminderWithDisplayNameVIN,
} from '../../../../../../../types/apiTypes';
import { MaintenanceReminder } from '../../../../../../../types/dbTypes';
import useApi from '../../../../../../../utils/AxiosWrapper';
import { MaintenanceFormData, MaintenanceKind, MaintenanceMode } from './types';

export const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/heif',
  'image/heic',
];

const MAX_FILE_SIZE_MB = 15;

// Ported verbatim from EditMaintenanceRecordModal so edit prefill matches.
const formatDateForInput = (date: Date | string | undefined) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

// Ported verbatim from EditMaintenanceRecordModal: turns a stored reminder
// back into the value the form input expects, per reminder_type.
const getReminderValue = (reminder: MaintenanceReminder) => {
  switch (reminder?.reminder_type) {
    case 'date':
      return reminder?.end_date;
    case 'days': {
      const reminderDate = new Date(reminder?.end_date ?? '');
      const currentDate = new Date();
      const diffTime = Math.abs(reminderDate.getTime() - currentDate.getTime());
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24)).toString();
    }
    case 'mileage':
      return (reminder?.end_mileage ?? 0) - (reminder?.starting_mileage ?? 0);
  }
};

type UseMaintenanceFormArgs = {
  kind: MaintenanceKind;
  mode: MaintenanceMode;
  existingRecord?: MaintenanceRecordWithDisplayName;
  existingReminder?: MaintenanceReminderWithDisplayNameVIN;
};

/**
 * Owns the react-hook-form instance plus the per-kind side state: file
 * selection/validation (record) and the linked-record fetch (reminder). Edit
 * mode prefills the form from the existing record/reminder. The validation
 * rules themselves live on each field's `register(...)` call inside the field
 * components, identical to the legacy modals.
 */
export function useMaintenanceForm({
  kind,
  mode,
  existingRecord,
  existingReminder,
}: UseMaintenanceFormArgs) {
  const form = useForm<MaintenanceFormData>();
  const { reset, watch } = form;
  const { callProtectedApi } = useApi();

  // ---- Record file state ----
  const [fileName, setFileName] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isNewFile, setIsNewFile] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const selectedFile = files[0];
      if (selectedFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        form.setError('file', {
          type: 'manual',
          message: `File size exceeds the ${MAX_FILE_SIZE_MB}MB limit`,
        });
        setFileName(null);
      } else if (!ALLOWED_FILE_TYPES.includes(selectedFile.type)) {
        form.setError('file', {
          type: 'manual',
          message:
            'Invalid file type. Only PDF, PNG, JPEG, HEIF, and HEIC are allowed.',
        });
        setFileName(null);
        setFile(null);
      } else {
        setFileName(selectedFile.name);
        setFile(selectedFile);
        setIsNewFile(true);
      }
    }
  };

  // ---- Reminder linked-record fetch state ----
  const [fetchedRecords, setFetchedRecords] = useState<
    MaintenanceRecordWithDisplayName[]
  >([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const pickedAsset = watch('pickedAssetFromList');

  const fetchMaintenanceRecords = async (
    asset_id: string,
    asset_type: string
  ) => {
    setLoadingRecords(true);
    try {
      const response = await callProtectedApi(
        `/maintenanceRecords/${asset_type}/${asset_id}`,
        'get',
        {},
        'Error occurred while fetching maintenance records for this asset.'
      );
      const data = response?.data?.data?.maintenance_records;
      if (data && data?.length > 0 && response?.status === HttpStatusCode.Ok) {
        setFetchedRecords(data);
      }
    } catch (e: any) {
      console.error(
        'Error occurred while fetching maintenance records for this asset: ',
        e
      );
    } finally {
      setLoadingRecords(false);
    }
  };

  useEffect(() => {
    if (kind === 'reminder' && pickedAsset && pickedAsset?.length > 0) {
      const assetArr = pickedAsset.split('#');
      fetchMaintenanceRecords(assetArr[0], assetArr[1]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedAsset]);

  // ---- Edit prefill ----
  useEffect(() => {
    if (kind === 'record' && mode === 'edit' && existingRecord) {
      reset({
        maintenanceType: existingRecord?.maintenance_type,
        description: existingRecord?.description,
        dateOfMaintenance: existingRecord?.date_of_maintenance
          ? formatDateForInput(existingRecord.date_of_maintenance)
          : undefined,
        setReminder:
          existingRecord?.reminder?.reminder_id &&
          existingRecord?.reminder?.reminder_id?.length > 0
            ? true
            : false,
        reminderType: existingRecord?.reminder?.reminder_type as
          | 'date'
          | 'days'
          | 'mileage'
          | undefined,
        reminderValue:
          existingRecord?.reminder?.reminder_type === 'date'
            ? formatDateForInput(existingRecord?.reminder?.end_date)
            : getReminderValue(existingRecord?.reminder)?.toString(),
      });
      setFileName(existingRecord?.file_key?.split('/')?.pop() || null);
      // Clear any file picked for a previously-edited record so it can't leak
      // into this record's submit (the modal instance is reused across edits).
      setFile(null);
      setIsNewFile(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingRecord, reset]);

  useEffect(() => {
    if (kind === 'reminder' && mode === 'edit' && existingReminder) {
      reset({
        maintenanceType: existingReminder.maintenance_type as
          | 'repairs'
          | 'tires'
          | 'preventative'
          | undefined,
        reminderType: existingReminder.reminder_type as
          | 'date'
          | 'days'
          | 'mileage',
        reminderValue:
          existingReminder?.reminder_type === 'date'
            ? formatDateForInput(existingReminder?.end_date)
            : existingReminder?.reminder_type === 'days'
              ? Math.ceil(
                  (new Date(existingReminder?.end_date ?? 0).getTime() -
                    new Date().getTime()) /
                    (1000 * 60 * 60 * 24)
                ).toString()
              : (
                  (existingReminder?.end_mileage ?? 0) -
                  (existingReminder?.starting_mileage ?? 0)
                ).toString(),
        recordId: existingReminder?.record_id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingReminder, reset]);

  return {
    form,
    // record file
    fileName,
    setFileName,
    file,
    setFile,
    isNewFile,
    handleFileChange,
    // reminder linked records
    fetchedRecords,
    setFetchedRecords,
    loadingRecords,
    pickedAsset,
  };
}
